import { useEffect } from "react";
import { useAppDispatch } from "@redux";
import { ErrorToast } from "./toasts/ErrorToast";
import { completeSocialSignIn } from "src/Module/Checkout/completeSocialSignIn";
import { readPendingSocialAction } from "src/Module/Checkout/pendingSocialAction";
import { shouldCoverSocialHandback } from "src/Module/Checkout/socialSignInCover";
import { SignInProgressCover } from "./Common/SignInProgressCover";
import { SignupNamesPrompt } from "./Common/SignupNamesPrompt";
import { ISignupNames } from "src/Module/Checkout/socialSignupNames";
import { parseAppleUserParam } from "src/Utilities/appleUserParam";
import { useState } from "react";

export const AppleOAuthHandler = () => {
    const dispatch = useAppDispatch();

    /**
     * Covers the page for the whole of the Apple hand-back, but only when the
     * sign-in began in the booking flow — see shouldCoverSocialHandback for why
     * an ordinary sign-in must be left uncovered.
     *
     * Seeded from the URL rather than set in the effect, so the cover is painted
     * on the first render instead of a frame after it.
     */
    const [showCover, setShowCover] = useState(() => {
        try {
            const hasToken = new URLSearchParams(window.location.search).has("id_token");
            return shouldCoverSocialHandback(hasToken, readPendingSocialAction());
        } catch {
            return false;
        }
    });

    /**
     * Set while the hand-off is waiting on the name prompt; holds the promise's
     * own resolver so submitting or skipping hands control straight back.
     *
     * Apple is the one provider that can never supply a name — it travels in the
     * first authorisation's form POST, which goes to the backend, and the
     * `id_token` we are handed carries none — so for a brand-new account this is
     * the only chance to get one before the upgrade mints a permanent branch
     * identity from it.
     */
    const [namesResolver, setNamesResolver] = useState<
        ((names: ISignupNames | null) => void) | null
    >(null);

    const requestSignupNames = () =>
        // Stored through an updater so React keeps the function as state rather
        // than calling it as one.
        new Promise<ISignupNames | null>((resolve) => setNamesResolver(() => resolve));

    const answerNamesPrompt = (names: ISignupNames | null) => {
        namesResolver?.(names);
        setNamesResolver(null);
    };

    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const idToken = urlParams.get("id_token");

        if (idToken) {
            const handleAppleLogin = async () => {
                try {
                    const { appleLoginAction, setJwt, initializeAccountDetail } =
                        await import("@redux");
                    // Apple sends the name beside the token on a first
                    // authorisation only, so it has to be forwarded now: the
                    // backend writes it when it creates the account, and nothing
                    // later can recover it.
                    const appleNames = parseAppleUserParam(urlParams.get("user"));
                    const res: any = await dispatch(
                        appleLoginAction({
                            access_token: idToken,
                            ...(appleNames ?? {}),
                        }),
                    );

                    const storedReturnPath = localStorage.getItem("appleLoginReturnPath");
                    if (res?.payload?.data?.accessToken) {
                        const { lsJwt } = await import("src/Utilities/storage");
                        lsJwt.setItem(res.payload.data);

                        // The guest's cart is found from the session itself, not
                        // guessed from the page they started on. Matching "/cart"
                        // or "/book-now" in the return path meant a sign-in begun
                        // from a product page merged nothing, and the items stayed
                        // stranded in the guest session.
                        dispatch(setJwt(res.payload.data));
                        await dispatch(initializeAccountDetail());

                        const storedPath = storedReturnPath;
                        localStorage.removeItem("appleLoginReturnPath");

                        const destination = await completeSocialSignIn({
                            dispatch,
                            accessToken: res.payload.data.accessToken,
                            provider: "Apple",
                            fallbackReturnPath: storedPath,
                            resolveSignupNames: requestSignupNames,
                        });

                        if (destination) {
                            const returnUrl = new URL(destination, window.location.origin);
                            window.location.href = `${returnUrl.pathname}${returnUrl.search}`;
                        } else {
                            // Nothing to go back to: at least strip the spent token
                            // from the URL so a remount does not post it again, and
                            // take the cover down, since nothing else now will.
                            const url = new URL(window.location.href);
                            url.searchParams.delete("id_token");
                            url.searchParams.delete("user");
                            window.history.replaceState(
                                {},
                                document.title,
                                url.pathname + url.search,
                            );
                            setShowCover(false);
                        }
                    } else {
                        const errorText = [
                            res?.payload?.error,
                            res?.payload?.title,
                            res?.payload?.message,
                        ]
                            .filter(Boolean)
                            .join(" ");
                        const isEmailError = /email|verified/i.test(errorText);
                        setShowCover(false);
                        ErrorToast({
                            Message: isEmailError
                                ? "Sign in with Apple couldn't share a verified email — try another method."
                                : "Sign in with Apple failed. Please try again.",
                        });
                    }
                } catch (error) {
                    console.error("Apple login error:", error);
                    setShowCover(false);
                    ErrorToast({ Message: "Sign in with Apple failed. Please try again." });
                }
            };

            handleAppleLogin();
        }
    }, [dispatch]);

    // Takes the cover's place rather than sitting over it: the visitor is being
    // asked to do something, not waiting on us.
    if (namesResolver) return <SignupNamesPrompt onResolve={answerNamesPrompt} />;

    if (!showCover) return null;

    return <SignInProgressCover />;
};
